import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AdminService } from '../../services/admin.service';
import { AdditionalExplanationDeletedModel } from '../../../shared/common/additionalExplanationDeleted.model';
import { AdditionalExplanationViewModel } from '../../viewmodels/additionalExplanationViewModel.model';
import { ReasonModel } from '../../../shared/common/viewmodels/reason.model';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { LocationViewModel } from '../../viewmodels/locationViewModel.model';
import { NgForm } from '@angular/forms';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'additionalExplanation',
  templateUrl: './additionalExplanation.component.html',
  styleUrls: ['./additionalExplanation.component.scss'],
})
export class AdditionalExplanationComponent implements OnInit, OnDestroy {
  additionalExplanations: AdditionalExplanationViewModel[];
  locations;
  activeLocations;
  locationTypes;
  public openedDelete = false;
  public openedEdit = false;
  public openedAdd = false;
  openedAddLocation = false;
  openedEditLocation = false;
  openedDeleteLocation = false;
  openModal = false;
  locationToDeleteId = 0;
  locationToEdit: LocationViewModel;
  createLocation = new LocationViewModel();
  explanationToDeleteId = 0;
  deletedExplanation: AdditionalExplanationDeletedModel;
  showLabel = false;
  showLabelCase = '';
  explanationToEditId: number;
  explanationToEdit: AdditionalExplanationViewModel;
  editedExplanation: AdditionalExplanationViewModel;
  createExplanation = new AdditionalExplanationViewModel();
  searchText = '';
  searchTextForEdit = '';
  allReasons: ReasonModel[] = [];
  filteredReasons: ReasonModel[] = [];
  // filteredReasonsEdit: ReasonModel[] = [];
  selectedReason: ReasonModel | null = null; // To store the selected reason
  selectedReasonId: number | null = null; // To store the selected reason's ID
  modalTitle: string;
  modalMessage: string;
  errors: string[] = [];

  private searchTerms = new Subject<string>();
  private searchSub: Subscription;

  constructor(
    private adminService: AdminService,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.getAdditionalExplanations();
    this.getLocationTypes();
    this.getLocations();
    this.getActiveLocations();
    this.getAllReasons();
    this.setupSearch();
  }

  getAdditionalExplanations() {
    this.adminService.getAdditionalExplanations().subscribe((result) => {
      if (result) {
        this.additionalExplanations = result;
      } else {
        console.log('Unable to find additional explanations');
      }
    });
  }

  getAdditionalExplanationById(explanationToEditId) {
    this.adminService.getAdditionalExplanationById(explanationToEditId).subscribe((result) => {
      if (result) {
        this.explanationToEdit = result;
        this.explanationToEdit.locations = this.activeLocations;
      } else {
        console.log('Unable to find additional explanation');
      }
    });
  }

  deleteExplanation(explanationId) {
    this.adminService.deleteExplanation(explanationId).subscribe((result) => {
      if (result === true) {
        this.deletedExplanation = result;
        this.getAdditionalExplanations();
        this.modalTitle = 'Success';
        this.modalMessage = 'Successfully deleted explanation';
        this.openModal = true;
      } else {
        this.modalTitle = 'Failed';
        this.modalMessage = 'Failed to delete explanation';
        this.openModal = true;
      }
    });
  }

  editExplanation(editedExplanation) {
    this.adminService.updateExplanation(editedExplanation).subscribe((result) => {
      if (result) {
        this.getAdditionalExplanations();
        this.modalTitle = 'Success';
        this.modalMessage = 'Successfully edited explanation';
        this.openModal = true;
      } else {
        this.modalTitle = 'Failed';
        this.modalMessage = 'Failed to edit explanation';
        this.openModal = true;
      }
    });
  }

  addAdditionalExplanation(createExplanation) {
    this.adminService.addAdditionalExplanation(createExplanation).subscribe((result) => {
      if (result) {
        this.getAdditionalExplanations();
        this.createExplanation = new AdditionalExplanationViewModel();
        this.modalTitle = 'Success';
        this.modalMessage = 'Successfully added additional explanation';
        this.openModal = true;
      } else {
        this.modalTitle = 'Failed';
        this.modalMessage = 'Failed to add additional explanation';
        this.openModal = true;
        this.createExplanation = null;
      }
    });
  }

  getAllReasons() {
    this.adminService.getAllReasons().subscribe(
      (reasons: ReasonModel[]) => {
        this.allReasons = reasons;
      },
      (error) => {
        console.error('Error fetching reasons:', error);
      }
    );
  }

  selectReason(id: number, reason: ReasonModel, mode: 'create' | 'edit') {
    this.selectedReason = reason;
    this.selectedReasonId = id;
    if (mode === 'create') {
      this.createExplanation.ricId = id;
      this.createExplanation.reason = reason.name;
    } else if (mode === 'edit') {
      this.explanationToEdit.ricId = id;
      this.explanationToEdit.reason = reason.name;
    }
  }

  getLocations() {
    this.adminService
      .getLocations()
      .subscribe({
        next: (result) => {
          if (result) {
            this.locations = result;

            for (const location of this.locations) {
              // Find the matching locationType
              const matchingLocationType = this.locationTypes.find(
                (type) => type.id === location.locationTypeId
              );
              // If a matching locationType is found, assign its name to location.locationType
              if (matchingLocationType) {
                location.locationType = matchingLocationType.name;
              }
            }
          } else {
            console.log('Unable to find locations');
          }
        },
        error: () => {},
        complete: () => {},
      })
      .add(() => {});
  }

  getActiveLocations() {
    this.adminService.getLocations().subscribe((result) => {
      if (result) {
        this.activeLocations = result.filter((location) => location.active === true);
        // console.log(this.activeLocations);
      } else {
        console.log('Unable to find locations');
      }
    });
  }

  getLocationTypes() {
    this.adminService.getLocationTypes().subscribe((result) => {
      if (result) {
        this.locationTypes = result;
      } else {
        console.log('Unable to find location types');
      }
    });
  }

  getLocationById(locationId) {
    this.adminService.getLocationById(locationId).subscribe((result) => {
      if (result) {
        this.locationToEdit = result;
        this.locationToEdit.locationTypes = this.locationTypes;
      } else {
        console.log('Unable to find location');
      }
    });
  }

  addLocation(createLocation) {
    this.adminService.addLocation(createLocation).subscribe((result) => {
      if (result) {
        this.createLocation = null;
        this.getLocations();
        this.modalTitle = 'Success';
        this.modalMessage = 'Successfully added new location';
        this.openModal = true;
      } else {
        this.modalTitle = 'Failed';
        this.modalMessage = 'Faied to add new location';
        this.openModal = true;
        console.log(result.reason);
        this.createLocation = null;
      }
    });
  }

  editLocation(editedLocation) {
    this.adminService.updateLocation(editedLocation).subscribe((result) => {
      if (result) {
        this.getLocations();
        this.locationToEdit.locationTypes = this.locationTypes;
        this.modalTitle = 'Success';
        this.modalMessage = 'Successfully edited location';
        this.openModal = true;
      } else {
        this.modalTitle = 'Failed';
        this.modalMessage = 'Failed to edit location';
        this.openModal = true;
        console.log(result.reason);
      }
    });
  }

  deleteLocation(locationId) {
    this.adminService.deleteLocation(locationId).subscribe((result) => {
      if (result === true) {
        this.getLocations();
        this.modalTitle = 'Success';
        this.modalMessage = 'Successfully deleted location';
        this.openModal = true;
      } else {
        this.modalTitle = 'Failed';
        this.modalMessage = 'Failed to delete location';
        this.openModal = true;
      }
    });
  }

  updateLocation(event: any) {
    this.explanationToEdit.locationName = event.target.value;

    // Retrieve locations and subscribe to the observable
    this.adminService.getLocations().subscribe((result) => {
      if (result) {
        this.locations = result;

        // Now that you have the locations, you can loop through them
        for (const location of this.locations) {
          if (location.name === this.explanationToEdit.locationName) {
            this.explanationToEdit.locationId = location.id;
            return; // Exit the loop since we found a match
          }
        }

        // Handle the case when no match is found
      } else {
        console.log('Unable to find locations');
      }
    });
  }

  updateLocationAdd(event: any) {
    this.createExplanation.locationName = event.target.value;

    // Retrieve locations and subscribe to the observable
    this.adminService.getLocations().subscribe((result) => {
      if (result) {
        this.locations = result;

        // Now that you have the locations, you can loop through them
        for (const location of this.locations) {
          if (location.name === this.createExplanation.locationName) {
            this.createExplanation.locationId = location.id;
            return; // Exit the loop since we found a match
          }
        }

        // Handle the case when no match is found
      } else {
        console.log('Unable to find locations');
      }
    });
  }

  updateLocationTypeAdd(event: any) {
    this.createLocation.locationType = event.target.value;

    // Retrieve locationTypes and subscribe to the observable
    this.adminService.getLocationTypes().subscribe((result) => {
      if (result) {
        this.locationTypes = result;

        // Now that you have the locationTypes, you can loop through them
        for (const locationType of this.locationTypes) {
          if (locationType.locationType === this.createLocation.locationType) {
            this.createLocation.locationTypeId = locationType.id;
            return; // Exit the loop since we found a match
          }
        }

        // Handle the case when no match is found
      } else {
        console.log('Unable to find location types');
      }
    });
  }

  updateLocationTypeEdit(event: any) {
    this.locationToEdit.locationType = event.target.value;

    // Retrieve locationTypes and subscribe to the observable
    this.adminService.getLocationTypes().subscribe((result) => {
      if (result) {
        this.locationTypes = result;

        // Now that you have the locationTypes, you can loop through them
        for (const locationType of this.locationTypes) {
          if (locationType.locationType === this.locationToEdit.locationType) {
            this.locationToEdit.locationTypeId = locationType.id;
            return; // Exit the loop since we found a match
          }
        }

        // Handle the case when no match is found
      } else {
        console.log('Unable to find location types');
      }
    });
  }

  openAddLocation() {
    if (!this.openedAddLocation) {
      this.createLocation.locationTypes = this.locationTypes;
      this.createLocation = new LocationViewModel();
      this.createLocation.active = true;
      this.openedAddLocation = true;
    }
  }

  // Function to close the Add Location modal
  closeAddLocation() {
    this.openedAddLocation = false;
    setTimeout(() => {
      this.createLocation = new LocationViewModel(); // Reset form after closing
    }, 100);
    // Reset or clear any form fields for adding a location.
  }

  saveAddLocation(form: NgForm) {
    if (form.valid) {
      this.addLocation(this.createLocation);
      this.closeAddLocation();
    } else {
      Object.keys(form.controls).forEach((field) => {
        const control = form.controls[field];
        control.markAsTouched();
      });
    }
  }

  // Function to open the Edit Location modal
  openEditLocation(locationId) {
    this.getLocationById(locationId);
    this.openedEditLocation = true;
  }

  saveEditLocation(form: NgForm) {
    if (form.valid) {
      this.editLocation(this.locationToEdit);
      this.openedEditLocation = false;
    } else {
      Object.keys(form.controls).forEach((field) => {
        const control = form.controls[field];
        control.markAsTouched();
      });
    }
  }

  // Function to close the Edit Location modal
  closeEditLocation() {
    this.openedEditLocation = false;
    // Reset or clear any form fields for editing the location.
  }

  // Function to open the Delete Location modal
  openDeleteLocation(locationId) {
    this.locationToDeleteId = locationId;
    this.openedDeleteLocation = true;
  }

  // Function to close the Delete Location modal
  closeDeleteLocation() {
    this.openedDeleteLocation = false;
  }

  // Function to confirm and perform the deletion of the location
  confirmDeleteLocation() {
    this.deleteLocation(this.locationToDeleteId);
    this.closeDeleteLocation();
  }

  public closeDelete() {
    this.openedDelete = false;
  }

  public openDelete(explanationId) {
    this.explanationToDeleteId = explanationId;
    this.openedDelete = true;
  }

  confirmDelete() {
    this.deleteExplanation(this.explanationToDeleteId);
    this.closeDelete();
  }

  public openEdit(explanationId: number) {
    this.getAdditionalExplanationById(explanationId);
    this.openedEdit = true;

    setTimeout(() => {
      this.searchText = ''; // Clears search text after modal opens
    }, 0);
  }

  saveEdit(form: NgForm) {
    if (form.valid) {
      this.editExplanation(this.explanationToEdit);
      this.closeEdit();
    } else {
      Object.keys(form.controls).forEach((field) => {
        const control = form.controls[field];
        control.markAsTouched();
      });
    }
  }

  public closeEdit() {
    this.openedEdit = false;
    this.clearSearchText();
  }

  // public openAdd() {
  //   this.getLocations();
  //   this.createExplanation.locations = this.activeLocations;
  //   this.openedAdd = true;
  // }

  openAdd() {
    this.getLocations();
    setTimeout(() => {
      this.createExplanation.locations = this.locations; // ✅ Use updated locations
      // console.log('Available locations for explanation:', this.createExplanation.locations);
      this.openedAdd = true;
    }, 300); // Small delay to ensure data is set
  }

  saveAdd(form: NgForm) {
    if (form.valid) {
      const currentDate = new Date();
      const formattedDateTime =
        `${currentDate.toDateString()}_${currentDate.toLocaleTimeString()}`.replace(/\s+/g, '_');
      this.createExplanation.textResourceId = `${this.createExplanation.name}_${formattedDateTime}`;
      this.addAdditionalExplanation(this.createExplanation);
      this.closeAdd();
    } else {
      Object.keys(form.controls).forEach((field) => {
        const control = form.controls[field];
        control.markAsTouched();
      });
    }
  }

  public closeAdd() {
    this.openedAdd = false;
    this.clearSearchText();
  }

  clearSearchText() {
    this.selectedReason = null;
    this.selectedReasonId = null;
    this.filteredReasons = null;
  }

  clearLocation(mode: 'create' | 'edit') {
    if (mode === 'create') {
      this.createExplanation.locationId = null;
    } else if (mode === 'edit') {
      this.explanationToEdit.locationId = null;
    }
  }

  clearReason(mode: 'create' | 'edit') {
    if (mode === 'create') {
      this.createExplanation.ricId = null;
      this.createExplanation.reason = '';
    } else if (mode === 'edit') {
      this.explanationToEdit.ricId = null;
      this.explanationToEdit.reason = '';
    }
  }

  onCloseModal() {
    this.openModal = false;
  }

  getOrderName(order: number): string {
    switch (order) {
      case 1:
        return 'start of explanations';
      case 2:
        return 'end of explanations';
      case 3:
        return 'after selected reason';
      default:
        return 'Unknown';
    }
  }

  trackByExplanationId(_index: number, explanation: AdditionalExplanationViewModel): number {
    return explanation.id;
  }

  ngOnDestroy() {
    this.searchSub?.unsubscribe();
  }

  private setupSearch() {
    this.searchSub = this.searchTerms
      .pipe(
        debounceTime(300), // Adjust debounce time as needed (e.g., 300 milliseconds)
        distinctUntilChanged(), // Ensure distinct values
        switchMap((term: string) => {
          // Perform your filtering logic here based on the term
          const filteredReasons = this.allReasons
            .filter((reason) => reason.caption.toLowerCase().includes(term.toLowerCase()))
            .slice(0, 5); // Limit results to 5

          this.filteredReasons = filteredReasons;
          // this.filteredReasonsEdit = filteredReasons;
          return [];
        })
      )
      .subscribe();
  }
}
